<template>
    <div class="development-container">
        <div class="development-title" v-html="$t(data.title)" />
        <div class="development-timetree">
            <div class="development-timeline-progress">
                <div
                    v-for="(item, index) in data.developmentDataList"
                    :id="`progress-item-${index}`"
                    :key="index"
                    class="development-timeline-progress-item"
                    :class="{ reverse: index % 2 === 0 }"
                    :style="`top: ${item.position}%`"
                >
                    <CommonComponentsSVGAboutUsDevelopmentPoint
                        class="development-timeline-progress-item-point"
                        :highlight="item.highlight"
                    />
                    <CommonComponentsSVGAboutUsDevelopmentIndicateLine
                        class="development-timeline-progress-item-line"
                        :class="{ reverse: index % 2 === 0 }"
                    />
                    <CommonComponentsSVGAboutUsDevelopmentIndicateLineMobile
                        class="development-timeline-progress-item-line-mobile"
                    />
                    <div class="development-timeline-progress-item-content">
                        {{ $t(item.year) }}
                        <ul>
                            <li v-for="(eventText, eIndex) in item.events" :key="eIndex">
                                <CommonComponentsSVGAboutUsDevelopmentCircle />
                                <span>{{ $t(eventText) }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
                <div ref="bar" class="development-timeline-progress-bar" />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { gsap } from 'gsap';
    import { ScrollTrigger } from 'gsap/all';
    import { matchMobile } from '~~/composables/useMatchRules.ts';

    gsap.registerPlugin(ScrollTrigger);
    const data = reactive({
        title: 'global_aboutus_introduction_development_title',
        developmentDataList: [
            {
                year: 'global_aboutus_introduction_development_year_1',
                events: ['global_aboutus_introduction_development_year_1_events_1'],
                position: 3,
                highlight: false,
            },
            {
                year: 'global_aboutus_introduction_development_year_2',
                events: [
                    'global_aboutus_introduction_development_year_2_events_1',
                    'global_aboutus_introduction_development_year_2_events_3',
                ],
                position: 15,
                highlight: false,
            },
            {
                year: 'global_aboutus_introduction_development_year_3',
                events: [
                    'global_aboutus_introduction_development_year_3_events_1',
                    'global_aboutus_introduction_development_year_3_events_2',
                ],
                position: 28,
                highlight: false,
            },
            {
                year: 'global_aboutus_introduction_development_year_4',
                events: ['global_aboutus_introduction_development_year_4_events_1'],
                position: 40,
                highlight: false,
            },
            {
                year: 'global_aboutus_introduction_development_year_5',
                events: ['global_aboutus_introduction_development_year_5_events_1'],
                position: 60,
                highlight: false,
            },
            {
                year: 'global_aboutus_introduction_development_year_6',
                events: ['global_aboutus_introduction_development_year_6_events_1'],
                position: 70,
                highlight: false,
            },
            {
                year: 'global_aboutus_introduction_development_year_7',
                events: ['global_aboutus_introduction_development_year_7_events_1'],
                position: 90,
                highlight: false,
            },
        ],
    });
    const bar = ref(null);

    onMounted(() => {
        // 动画
        const animation = () => {
            const element = gsap.utils.toArray('.development-timeline-progress-item') as HTMLElement[];
            gsap.timeline({
                scrollTrigger: {
                    // markers: { startColor: 'green', endColor: 'red', fontSize: '12px' },
                    trigger: '.development-timeline-progress',
                    start: 'top 80%',
                    end: 'bottom center',
                    scrub: true,
                },
            })
                .set(bar.value, { height: 0, transformOrigin: 'top center' })
                .to(bar.value, {
                    height: '100%',
                    ease: 'none',
                    duration: 3,
                })
                .fromTo(
                    element,
                    {
                        opacity: 0,
                    },
                    {
                        opacity: 1,
                        duration: 0.6,
                        stagger: 0.4,
                    },
                    '<',
                );
        };

        const ifMatched = matchMobile();
        const reverse = document.querySelectorAll('.development-timeline-progress-item');
        const itemAndLine = gsap.utils.toArray(reverse) as Array<HTMLElement>;

        if (!ifMatched) {
            // console.log('pc');
            animation();
        } else {
            // 移动端取反
            itemAndLine.forEach((item) => {
                item.classList.remove('reverse');
            });
            const element = gsap.utils.toArray('.development-timeline-progress-item') as HTMLElement[];
            gsap.timeline({
                scrollTrigger: {
                    // markers: { startColor: 'green', endColor: 'red', fontSize: '12px' },
                    trigger: '.development-timeline-progress',
                    start: 'top 80%',
                    end: 'bottom center',
                    scrub: true,
                },
            })
                .set(bar.value, { height: 0, transformOrigin: 'top center' })
                .to(bar.value, {
                    height: '100%',
                    ease: 'none',
                    duration: 3,
                })
                .fromTo(
                    element,
                    {
                        opacity: 0,
                    },
                    {
                        opacity: 0.8, // 0.8透明度
                        duration: 0.6,
                        stagger: 0.45,
                    },
                    '<',
                );
        }

        addSlideInUp('.development-title');
    });
</script>

<style lang="less" scoped>
    @process-bar-width: 8px;
    @process-width: 6px;
    .development-container {
        display: flex;
        flex-direction: column;
        height: .convert(2000px) [ @vw];
        border-bottom-right-radius: 20px;
        border-bottom-left-radius: 20px;
        background: #161616;
        @media (max-width: @screen-mobile-width) {
            height: .convert(1400px) [ @vw-mobile];
        }
        .development {
            &-title {
                // height: 100%;
                font-size: .convert(@font-size-title-lg) [ @vw]; //64px
                line-height: normal;
                text-align: center;
                margin: .convert(100px) [ @vw] auto;
                @media (max-width: @screen-mobile-width) {
                    font-size: .convert(32px) [ @vw-mobile];
                    text-align: left;
                    margin: .convert(30px) [ @vw-mobile] .convert(20px) [ @vw-mobile];
                }
            }
            &-timetree {
                display: flex;
                height: 100%;
                justify-content: center;
                margin-bottom: .convert(150px) [ @vw];
                @media (max-width: @screen-mobile-width) {
                    // flex-direction: column;
                    justify-content: flex-start;
                    // padding: .convert(30px) [ @vw-mobile] .convert(22px) [ @vw-mobile];
                    margin-bottom: .convert(40px) [ @vw-mobile];
                    margin-left: .convert(18px) [ @vw-mobile];
                }
                .development-timeline-progress {
                    z-index: 0;
                    width: @process-width;
                    border-radius: calc(@process-width / 2);
                    height: 100%;
                    background-color: #464646;
                    position: relative;
                    @media (max-width: @screen-mobile-width) {
                        width: .convert(4px) [ @vw-mobile];
                        border-radius: .convert(2px) [ @vw-mobile];
                        //移动端下每一项的定位
                        #progress-item-1 {
                            top: 13% !important;
                        }
                        #progress-item-2 {
                            top: 33% !important;
                        }
                        #progress-item-3 {
                            top: 50% !important;
                        }
                        #progress-item-4 {
                            top: 65% !important;
                        }
                        #progress-item-5 {
                            top: 75% !important;
                        }
                        #progress-item-6 {
                            top: 90% !important;
                        }
                    }

                    .development-timeline-progress-item {
                        width: fit-content;
                        display: flex;
                        position: absolute;
                        left: 50%;
                        right: auto;
                        &-point {
                            position: relative;
                            z-index: 2;
                            transform: translate(-50%);
                        }
                        &-line,
                        &-line-mobile {
                            position: absolute;
                            z-index: 1;
                            left: 0;
                            bottom: 50%;
                            width: .convert(143px) [ @vw];
                            display: block;
                            @media (max-width: @screen-mobile-width) {
                                transform: translateY(10%);
                                display: none;
                                // font-size: .convert(@font-size-xl) [ @vw-mobile]; //24px
                            }
                        }
                        &-line-mobile {
                            display: none;
                            @media (max-width: @screen-mobile-width) {
                                display: block;
                            }
                        }
                        &-content {
                            position: absolute;
                            transform: translate(0, -80%);
                            left: .convert(150px) [ @vw];
                            top: 50%;
                            font-size: .convert(@font-size-title) [ @vw];
                            @media (max-width: @screen-mobile-width) {
                                font-size: .convert(@font-size-xl) [ @vw-mobile]; //24px
                            }
                            ul {
                                position: absolute;
                                left: 0;
                                right: auto;
                                max-width: 510px;
                                width: .convert(510px) [ @vw]; //放下更宽文字
                                list-style-type: circle;
                                font-size: .convert(16px) [ @vw];
                                @media (max-width: @screen-mobile-width) {
                                    width: 72vw;
                                    color: #ffffff;
                                }

                                li {
                                    display: flex;
                                    gap: 8px;
                                    align-items: flex-start;
                                    line-height: .convert(25.6px) [ @vw];
                                    font-family: 'HarmonyOS Sans SC';
                                    @media (max-width: @screen-mobile-width) {
                                        font-size: .convert(@font-size-xm) [ @vw-mobile]; //12px
                                        line-height: 160%; /* 19.2px */
                                    }
                                    .svg-circle-container {
                                        height: .convert(25.6px) [ @vw];
                                        @media (max-width: @screen-mobile-width) {
                                            height: .convert(4px) [ @vw-mobile];
                                            margin-top: .convert(8px) [ @vw-mobile];
                                        }
                                    }
                                    &:nth-child(n + 2) {
                                        margin-top: 18px;
                                    }
                                }
                            }
                        }
                    }
                    .reverse {
                        flex-direction: row-reverse;
                        left: auto;
                        right: 50%;
                        .development-timeline-progress-item-point {
                            transform: translate(50%);
                        }
                        .development-timeline-progress-item-line {
                            left: auto;
                            right: 0;
                            display: block;
                            @media (max-width: @screen-mobile-width) {
                                display: none;
                            }
                        }
                        .development-timeline-progress-item-line-mobile {
                            left: auto;
                            right: 0;
                            display: none;
                            @media (max-width: @screen-mobile-width) {
                                display: block;
                            }
                        }
                        .development-timeline-progress-item-content {
                            left: auto;
                            right: .convert(150px) [ @vw];
                            text-align: right;
                            ul {
                                left: auto;
                                right: 0;
                                list-style-type: circle;
                                text-align: right;
                                li {
                                    flex-direction: row-reverse;
                                }
                            }
                        }
                    }
                    //bar
                    .development-timeline-progress-bar {
                        z-index: -1;

                        width: @process-bar-width;
                        border-radius: calc(@process-bar-width / 2);
                        height: 0;
                        background-image: linear-gradient(#37e6fe, #0032ff);
                        // background-size: 100% 0%;
                        background-repeat: no-repeat;

                        display: block;
                        position: absolute;
                        top: 0;
                        left: 50%;
                        transform: translate(-50%);
                        @media (max-width: @screen-mobile-width) {
                            width: .convert(4px) [ @vw-mobile];
                            border-radius: .convert(2px) [ @vw-mobile];
                        }
                    }
                }
            }
        }
    }
</style>
